import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Form, Formik, useFormikContext } from "formik";
import { toast } from "react-toastify";

import { orderFormValidationSchema } from "../../schemas/orderForm";
import useItemService from "../../services/api/itemService";
import useOrderFormService from "../../services/api/orderFormService";
import currencyFormatter from "../../util/currencyFormatter";
import { deepMerge, omitKeysFromObject } from "../../util/jsObjects";
import { getNextDate } from "../../util/dateHelpers";
import { enums } from "../../constants/enum";

import { DownloadIcon, PasteIcon } from "../../assets/icons";
import { Button, DataRow, PageTitle, Section, Tab } from "../../components";
import {
  Checkbox,
  DatePicker,
  EyeMeasurementInput,
  Radio,
  SelectInput,
  TextInput,
} from "../../components/forms";

import NewCustomerTab from "./NewCustomerTab";
import ExistingCustomersTab from "./ExistingCustomersTab";
import SelectedCustomer from "./SelectedCustomer";

import "./PrescriptionFormPage.scss";
import useAuthentication from "../../hooks/auth";
import useOrderService from "../../services/api/orderService";

const STORAGE_KEY = enums.PRESCRIPTION_FORM_STORAGE_KEY;
const initialValues = {
  customer: "",
  eyeCheckingNumber: "",
  hb: "",
  CR39: false,
  PHOTO_GREY: false,
  MULTI_COATED: false,
  PROGRESSIVE: false,
  EASY_FIT: false,
  distance: {
    right: {
      sph: null,
      cyl: null,
      axis: 0,
    },
    left: {
      sph: null,
      cyl: null,
      axis: 0,
    },
  },
  near: {
    right: {
      sph: null,
      cyl: null,
      axis: 0,
    },
    left: {
      sph: null,
      cyl: null,
      axis: 0,
    },
  },
  frame: {
    selectedFrame: "",
    pd: "",
  },
  lense: {
    left: {
      selectedLense: "",
      diameter: 0,
    },
    right: {
      selectedLense: "",
      diameter: 0,
    },
  },
  discount: 0,
  testDate: "",
  orderSchedule: enums.PLACE_ORDER.LATER,
  paymentMethod: enums.PAYMENT_METHODS.CASH,
  paymentType: enums.PAYMENT_TYPES.ADVANCE,
};

// Save form data to localStorage
const saveFormDataToStorage = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save form data to localStorage:", error);
    // Handle quota exceeded or other errors
    if (error.name === "QuotaExceededError") {
      toast.error("Storage quota exceeded. Unable to save form data.");
    }
  }
};

// Load form data from localStorage
const loadFormDataFromStorage = () => {
  try {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      // Deep merge saved data with initial values to ensure all fields exist
      return deepMerge(initialValues, parsedData);
    }
  } catch (error) {
    console.error("Failed to load form data from localStorage:", error);
    localStorage.removeItem(STORAGE_KEY); // Clear corrupted data
  }
  return initialValues;
};

function PrescriptionFormPage() {
  const { profile } = useAuthentication();
  const { getItemById, queryKey } = useItemService();
  const {
    getNewOrderFormNumber,
    queryKey: orderFormQueryKey,
    createOrderForm,
  } = useOrderFormService();
  const { createOrder } = useOrderService();
  const [activeCustomerTab, setActiveCustomerTab] =
    useState("Existing Customers");
  const [formData, setFormData] = useState(loadFormDataFromStorage);

  // Query for new order form number
  const { data: newOrderFormNumber, refetch } = useQuery({
    queryKey: [orderFormQueryKey, "new-order-form-number"],
    queryFn: () => getNewOrderFormNumber(),
    enabled: true,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // Query for Right Lens
  const { data: rightLensData } = useQuery({
    queryKey: [queryKey, "item-lookup", formData.lense.right.selectedLense],
    queryFn: () => getItemById(formData.lense.right.selectedLense),
    enabled: !!formData.lense.right.selectedLense,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Query for Left Lens
  const { data: leftLensData } = useQuery({
    queryKey: [queryKey, "item-lookup", formData.lense.left.selectedLense],
    queryFn: () => getItemById(formData.lense.left.selectedLense),
    enabled: !!formData.lense.left.selectedLense,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Query for Frame
  const { data: frameData } = useQuery({
    queryKey: [queryKey, "item-lookup", formData.frame.selectedFrame],
    queryFn: () => getItemById(formData.frame.selectedFrame),
    enabled: !!formData.frame.selectedFrame,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const createNewOrderMutation = useMutation({
    mutationFn: createOrder,
    onSuccess: (payload) => {
      toast.success(payload?.message || "Order created successfully");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
  });

  const createOrderFormMutation = useMutation({
    mutationFn: createOrderForm,
    onSuccess: (payload) => {
      // Clear localStorage after successful submission
      localStorage.removeItem(STORAGE_KEY);
      if (formData.orderSchedule === enums.PLACE_ORDER.NOW) {
        createNewOrderMutation.mutate({
          payload: {
            orderForm: payload?.orderForm,
            payments: [
              {
                amount:
                  formData.paymentType === enums.PAYMENT_TYPES.ADVANCE
                    ? formData.advance
                    : parseInt(frameData?.sellingPrice || 0) +
                      parseInt(rightLensData?.sellingPrice || 0) +
                      parseInt(leftLensData?.sellingPrice || 0) -
                      parseInt(formData?.discount || 0) -
                      parseInt(formData?.advance || 0),
                method: formData.paymentMethod,
                type: formData.paymentType,
              },
            ],
          },
        });
      }
      setFormData(initialValues);
      toast.success(payload?.message);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
    onSettled: () => {
      refetch();
    },
  });

  // Save to localStorage whenever formData changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveFormDataToStorage(formData);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData]);

  useEffect(() => {
    if (
      rightLensData &&
      rightLensData.category !== enums.ITEM_CATEGORIES.LENSE
    ) {
      toast.error("Invalid Item Type. Expected a Lense Item.");
    }
  }, [rightLensData]);

  useEffect(() => {
    if (leftLensData && leftLensData.category !== enums.ITEM_CATEGORIES.LENSE) {
      toast.error("Invalid Item Type. Expected a Lense Item.");
    }
  }, [leftLensData]);

  useEffect(() => {
    if (frameData && frameData.category !== enums.ITEM_CATEGORIES.FRAME) {
      toast.error("Invalid Item Type. Expected a Frame Item.");
    }
  }, [frameData]);

  // Set new order form number
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      eyeCheckingNumber: newOrderFormNumber?.nextOrderFormNumber || "",
    }));
  }, [newOrderFormNumber]);

  async function handleSubmit() {
    try {
      await orderFormValidationSchema.validate(formData, {
        abortEarly: true,
      });
    } catch (error) {
      toast.error(error.message);
      return;
    }

    let payload =
      formData.orderSchedule === enums.PLACE_ORDER.LATER
        ? omitKeysFromObject(formData, [
            "orderSchedule",
            "paymentMethod",
            "paymentType",
            "frame",
            "lense",
            "advance",
          ])
        : omitKeysFromObject(formData, [
            "orderSchedule",
            "paymentMethod",
            "paymentType",
            "advance",
          ]);
    createOrderFormMutation.mutate({ payload });
  }

  return (
    <div className="page prescription-form-page">
      <PageTitle text="Prescription Form" />

      <Section
        title="Prescription Details"
        className="prescription-details-section"
      >
        <Formik
          initialValues={{
            eyeCheckingNumber: formData.eyeCheckingNumber,
            testDate: formData.testDate,
          }}
          enableReinitialize
          onSubmit={() => {}}
        >
          <Form>
            <div className="input-row">
              <TextInput
                name="eyeCheckingNumber"
                label="Prescription Number"
                placeholder="Type here"
              />
            </div>

            <div className="input-row">
              <div className="date-pic-input">
                <span>Date</span>
                <DatePicker
                  name="testDate"
                  label="Date"
                  max={getNextDate(0).toISOString().split("T")[0]}
                />
              </div>

              <div>
                <span>Created By</span>
                <p>
                  <span>{profile?.role}</span> {profile?.fullName}
                </p>
              </div>
            </div>
            <AutoUpdateFormData setFormData={setFormData} />
          </Form>
        </Formik>
      </Section>

      {formData.customer ? (
        <SelectedCustomer
          setSelectedCustomerId={setFormData}
          customerId={formData.customer}
        />
      ) : (
        <Section title="Customer Details" className="customer-details-section">
          <Tab
            tabs={["Existing Customers", "New Customer"]}
            activeTab={activeCustomerTab}
            onTabChange={setActiveCustomerTab}
            className="customer-details-tab"
          >
            {activeCustomerTab === "Existing Customers" && (
              <ExistingCustomersTab onSelect={setFormData} />
            )}
            {activeCustomerTab === "New Customer" && (
              <NewCustomerTab onSelect={setFormData} />
            )}
          </Tab>
        </Section>
      )}

      <Formik
        initialValues={{
          hb: formData.hb,
          CR39: formData.CR39,
          PHOTO_GREY: formData.PHOTO_GREY,
          MULTI_COATED: formData.MULTI_COATED,
          PROGRESSIVE: formData.PROGRESSIVE,
          EASY_FIT: formData.EASY_FIT,
          distance: formData.distance,
          near: formData.near,
        }}
        enableReinitialize
        onSubmit={() => {}}
      >
        <Form>
          <Section
            title="Eye Measurements"
            className="eye-measurements-section"
          >
            <div className="input-group">
              <TextInput
                label="Half Bifocal (H.B)"
                placeholder="Type here"
                name="hb"
                prefix="+"
              />

              <div className="lens-options">
                <span>Lens options</span>

                <div>
                  <div className="checkbox-group">
                    <Checkbox name="CR39" label="CR39" />
                    <Checkbox name="PHOTO_GREY" label="Photo Grey" />
                    <Checkbox name="MULTI_COATED" label="Multi Coated" />
                  </div>

                  <div className="checkbox-group">
                    <Checkbox name="PROGRESSIVE" label="Progressive" />
                    <Checkbox name="EASY_FIT" label="Easy Fit" />
                  </div>
                </div>
              </div>
            </div>

            <div className="table-group">
              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>R</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput name="distance.right.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="distance.right.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          name="distance.right.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput name="near.right.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="near.right.cyl" />
                      </td>
                      <td>
                        <TextInput className="tinput" name="near.right.axis" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>L</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput name="distance.left.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="distance.left.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          name="distance.left.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput name="near.left.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="near.left.cyl" />
                      </td>
                      <td>
                        <TextInput className="tinput" name="near.left.axis" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
          <AutoUpdateFormData setFormData={setFormData} />
        </Form>
      </Formik>
      <Formik
        initialValues={{ orderSchedule: formData.orderSchedule }}
        enableReinitialize
        onSubmit={() => {}}
      >
        <Form>
          <Section title="Place Order" className="place-order-section">
            <Radio
              name="orderSchedule"
              value={enums.PLACE_ORDER.NOW}
              label="Now"
            />
            <Radio
              name="orderSchedule"
              value={enums.PLACE_ORDER.LATER}
              label="Later"
            />
          </Section>
          <AutoUpdateFormData setFormData={setFormData} />
        </Form>
      </Formik>

      {formData.orderSchedule === enums.PLACE_ORDER.NOW ? (
        <>
          <Section title="Lens Details" className="lens-details-section">
            <div className="right-lens">
              <h3>Right Lens</h3>

              <Formik
                initialValues={{
                  lense: {
                    right: {
                      selectedLense: formData.lense.right.selectedLense,
                    },
                  },
                }}
                enableReinitialize
                onSubmit={() => {}}
              >
                <Form>
                  <div className="select-form">
                    <TextInput
                      label="Item Code"
                      placeholder="Type here"
                      name="lense.right.selectedLense"
                    />
                    <PasteButton name="lense.right.selectedLense" />
                    <AutoUpdateFormData setFormData={setFormData} />
                  </div>
                </Form>
              </Formik>

              <div className="lens-table">
                <div className="table-container">
                  <table>
                    <tbody>
                      <tr>
                        <td
                          rowSpan={
                            rightLensData?.attributes?.coating ===
                            enums.LENSE_COATINGS.PHOTO_GREY
                              ? 7
                              : 6
                          }
                          className="table-title"
                        >
                          <p>LENS</p>
                        </td>
                        <td className="table-sub-title">Brand</td>
                        <td>{rightLensData?.brand || " - "}</td>
                      </tr>

                      <tr>
                        <td className="table-sub-title">Type</td>
                        <td>
                          {rightLensData?.attributes?.lense_type || " - "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Color</td>
                        <td>{rightLensData?.attributes?.coating || " - "}</td>
                      </tr>

                      {rightLensData?.attributes?.coating ===
                      enums.LENSE_COATINGS.PHOTO_GREY ? (
                        <tr>
                          <td className="table-sub-title">Transition</td>
                          <td>
                            {rightLensData?.attributes?.transition || " - "}
                          </td>
                        </tr>
                      ) : null}
                      <tr>
                        <td className="table-sub-title">SEG</td>
                        <td>
                          24 x{" "}
                          {rightLensData?.attributes?.seg
                            ? `${rightLensData?.attributes?.seg} mm`
                            : "  "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Diameter</td>
                        <td>
                          <Formik
                            initialValues={{
                              lense: {
                                right: {
                                  diameter: formData.lense.right.diameter,
                                },
                              },
                            }}
                            enableReinitialize
                            onSubmit={() => {}}
                          >
                            <Form>
                              <div className="select-form">
                                <TextInput
                                  name="lense.right.diameter"
                                  placeholder="mm"
                                  type="number"
                                  className="prescription-input"
                                />
                              </div>
                              <AutoUpdateFormData setFormData={setFormData} />
                            </Form>
                          </Formik>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Price</td>
                        <td>
                          {currencyFormatter(
                            parseInt(rightLensData?.sellingPrice || 0),
                          )}{" "}
                          LKR
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="separator"></div>

            <div className="left-lens">
              <h3>Left Lens</h3>

              <Formik
                initialValues={{
                  lense: {
                    left: {
                      selectedLense: formData.lense.left.selectedLense,
                    },
                  },
                }}
                enableReinitialize
                onSubmit={() => {}}
              >
                <Form>
                  <div className="select-form">
                    <TextInput
                      label="Item Code"
                      placeholder="Type here"
                      name="lense.left.selectedLense"
                    />
                    <PasteButton name="lense.left.selectedLense" />
                    <AutoUpdateFormData setFormData={setFormData} />
                  </div>
                </Form>
              </Formik>

              <div className="lens-table">
                <div className="table-container">
                  <table>
                    <tbody>
                      <tr>
                        <td
                          rowSpan={
                            leftLensData?.attributes?.coating ===
                            enums.LENSE_COATINGS.PHOTO_GREY
                              ? 7
                              : 6
                          }
                          className="table-title"
                        >
                          <p>LENS</p>
                        </td>
                        <td className="table-sub-title">Brand</td>
                        <td>{leftLensData?.brand || " - "}</td>
                      </tr>

                      <tr>
                        <td className="table-sub-title">Type</td>
                        <td>{leftLensData?.attributes?.lense_type || " - "}</td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Color</td>
                        <td>{leftLensData?.attributes?.coating || " - "}</td>
                      </tr>

                      {leftLensData?.attributes?.coating ===
                      enums.LENSE_COATINGS.PHOTO_GREY ? (
                        <tr>
                          <td className="table-sub-title">Transition</td>
                          <td>
                            {leftLensData?.attributes?.transition || " - "}
                          </td>
                        </tr>
                      ) : null}
                      <tr>
                        <td className="table-sub-title">SEG</td>
                        <td>
                          24 x{" "}
                          {leftLensData?.attributes?.seg
                            ? `${leftLensData?.attributes?.seg} mm`
                            : "  "}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Diameter</td>
                        <td>
                          <Formik
                            initialValues={{
                              lense: {
                                left: {
                                  diameter: formData.lense.left.diameter,
                                },
                              },
                            }}
                            enableReinitialize
                            onSubmit={() => {}}
                          >
                            <Form>
                              <div className="select-form">
                                <TextInput
                                  name="lense.left.diameter"
                                  placeholder="mm"
                                  type="number"
                                  className="prescription-input"
                                />
                              </div>
                              <AutoUpdateFormData setFormData={setFormData} />
                            </Form>
                          </Formik>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Price</td>
                        <td>
                          {currencyFormatter(
                            parseInt(leftLensData?.sellingPrice || 0),
                          )}{" "}
                          LKR
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Frame Details" className="frame-details-section">
            <Formik
              initialValues={{
                frame: {
                  selectedFrame: formData.frame.selectedFrame,
                },
              }}
              enableReinitialize
              onSubmit={() => {}}
            >
              <Form>
                <div className="select-form">
                  <TextInput
                    label="Item Code"
                    placeholder="Type here"
                    name="frame.selectedFrame"
                  />
                  <PasteButton name="frame.selectedFrame" />
                  <AutoUpdateFormData setFormData={setFormData} />
                </div>
              </Form>
            </Formik>

            <div className="frame-table">
              <div className="table-container">
                <table>
                  <tbody>
                    <tr>
                      <td rowSpan={8} className="table-title">
                        <p>FRAME</p>
                      </td>
                      <td className="table-sub-title">Modal</td>
                      <td colSpan={2}>
                        {frameData?.attributes?.model_number || " - "}
                      </td>
                    </tr>

                    <tr>
                      <td rowSpan={3} className="table-sub-title">
                        Size
                      </td>
                      <td className="table-sub-sub-title">Lens diagonal</td>
                      <td>
                        {frameData?.attributes?.size?.lens_diagonal || " - "}
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-sub-title">Bridge size</td>
                      <td>{frameData?.attributes?.size?.bridge || " - "}</td>
                    </tr>

                    <tr>
                      <td className="table-sub-sub-title">Arm Length</td>
                      <td>
                        {frameData?.attributes?.size?.arm_length || " - "}
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">Color</td>
                      <td colSpan={2}>
                        {frameData?.attributes?.color || " - "}
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Material</td>
                      <td colSpan={2}>
                        {frameData?.attributes?.material || " - "}
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">P.D</td>
                      <td colSpan={2}>
                        <Formik
                          initialValues={{
                            frame: {
                              pd: formData.frame.pd,
                            },
                          }}
                          enableReinitialize
                          onSubmit={() => {}}
                        >
                          <Form>
                            <div className="select-form">
                              <TextInput
                                name="frame.pd"
                                placeholder="mm"
                                type="number"
                                className="prescription-input"
                              />
                            </div>
                            <AutoUpdateFormData setFormData={setFormData} />
                          </Form>
                        </Formik>
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Price</td>
                      <td colSpan={2}>
                        {currencyFormatter(
                          parseInt(frameData?.sellingPrice || 0),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          <Section title="Charges" className="charges-section">
            <div className="left-bx">
              <div className="charges-table">
                <div className="table-container">
                  <table>
                    <tr>
                      <td className="table-sub-title">Frame</td>
                      <td>
                        {currencyFormatter(
                          parseInt(frameData?.sellingPrice || 0),
                        )}{" "}
                        LKR
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">Lens</td>
                      <td>
                        {currencyFormatter(
                          parseInt(rightLensData?.sellingPrice || 0) +
                            parseInt(leftLensData?.sellingPrice || 0),
                        )}{" "}
                        LKR
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">Discount</td>
                      <td>
                        <Formik
                          initialValues={{ discount: formData.discount }}
                          enableReinitialize
                          onSubmit={() => {}}
                        >
                          <Form>
                            <div>
                              <TextInput
                                name="discount"
                                type="number"
                                className="prescription-input"
                              />
                              <AutoUpdateFormData setFormData={setFormData} />
                            </div>
                          </Form>
                        </Formik>
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Total</td>
                      <td>
                        <h3>
                          {currencyFormatter(
                            parseInt(frameData?.sellingPrice || 0) +
                              parseInt(rightLensData?.sellingPrice || 0) +
                              parseInt(leftLensData?.sellingPrice || 0) -
                              parseInt(formData?.discount || 0),
                          )}{" "}
                          LKR
                        </h3>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>

            <div className="right-bx">
              <Formik
                initialValues={{
                  paymentMethod: formData.paymentMethod,
                  paymentType: formData.paymentType,
                  advance: formData.advance || "",
                }}
                enableReinitialize
                onSubmit={() => {}}
              >
                <Form>
                  <div className="charges-form">
                    <div className="radio-group">
                      <Radio
                        name="paymentType"
                        value={enums.PAYMENT_TYPES.FULL_PAYMENT}
                        label="Full Payment"
                      />
                      <Radio
                        name="paymentType"
                        value={enums.PAYMENT_TYPES.ADVANCE}
                        label="Two Payment"
                      />
                    </div>
                    {formData.paymentType === enums.PAYMENT_TYPES.ADVANCE ? (
                      <DataRow
                        labelLeft="Advance"
                        dataLeft={
                          <div>
                            <TextInput
                              name="advance"
                              className="prescription-input"
                            />
                          </div>
                        }
                      />
                    ) : null}
                    <DataRow
                      labelLeft={
                        formData.paymentType === enums.PAYMENT_TYPES.ADVANCE
                          ? "Balance"
                          : "Full Payment"
                      }
                      dataLeft={
                        <p className="full-payment">
                          {currencyFormatter(
                            parseInt(frameData?.sellingPrice || 0) +
                              parseInt(rightLensData?.sellingPrice || 0) +
                              parseInt(leftLensData?.sellingPrice || 0) -
                              parseInt(formData?.discount || 0) -
                              parseInt(formData?.advance || 0),
                          )}
                        </p>
                      }
                    />

                    <SelectInput
                      label="Payment Method"
                      name="paymentMethod"
                      values={[
                        { label: "CASH", value: enums.PAYMENT_METHODS.CASH },
                        { label: "CARD", value: enums.PAYMENT_METHODS.CARD },
                        {
                          label: "BANK",
                          value: enums.PAYMENT_METHODS.BANK_TRANSFER,
                        },
                      ]}
                    />
                  </div>
                  <AutoUpdateFormData setFormData={setFormData} />
                </Form>
              </Formik>
            </div>
          </Section>
        </>
      ) : null}

      <Button
        type="button"
        text="Submit"
        icon={<DownloadIcon />}
        onClick={handleSubmit}
      />
    </div>
  );
}

function AutoUpdateFormData({ setFormData }) {
  const { values } = useFormikContext();
  useEffect(() => {
    setFormData((prev) => deepMerge(prev, values));
  }, [values, setFormData]);
}

function PasteButton({ name }) {
  const { setFieldValue } = useFormikContext();
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setFieldValue(name, text);
    } catch (err) {
      console.error("Failed to read clipboard", err);
    }
  };

  return (
    <Button
      type="button"
      text="Paste"
      icon={<PasteIcon />}
      onClick={handlePaste}
    />
  );
}

export default PrescriptionFormPage;
